'use client';
import { cn } from '../cn';

interface DataTablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  ariaLabel?: string;
}

/**
 * Footer paginare cu navigare prev/next, săritură la pagină + selector page size.
 * Generic — folosit de DataTable pentru paginare client- sau server-side.
 */
export function DataTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  ariaLabel = 'Paginare',
}: DataTablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Trim list of visible pages: always show first, last, current ±1, with ellipses
  const visible: (number | '...')[] = [];
  if (totalPages <= 7) {
    for (let p = 1; p <= totalPages; p++) visible.push(p);
  } else {
    visible.push(1);
    if (page > 3) visible.push('...');
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) visible.push(p);
    if (page < totalPages - 2) visible.push('...');
    visible.push(totalPages);
  }

  return (
    <nav
      aria-label={ariaLabel}
      className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 bg-neutral-50 px-4 py-2.5 text-xs text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400"
    >
      {/* Stânga — range „Afișare X-Y din Z" (acelasi format ca /facturi) */}
      <div className="tabular-nums">
        Afișare <b className="text-neutral-900 dark:text-neutral-100">{start}–{end}</b> din <b className="text-neutral-900 dark:text-neutral-100">{total}</b>
      </div>

      {/* Centru — page numbers cu prev/next */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Pagina anterioară"
          className={cn(
            'rounded border border-neutral-200 bg-white px-2 py-1 transition-colors dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
            page <= 1 ? 'cursor-not-allowed opacity-40' : 'hover:bg-neutral-100 dark:hover:bg-neutral-700',
          )}
        >‹</button>
        {visible.map((p, i) => p === '...' ? (
          <span key={`e${i}`} className="px-1 text-neutral-400 dark:text-neutral-500" aria-hidden="true">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-label={`Pagina ${p}`}
            aria-current={p === page ? 'page' : undefined}
            className={cn(
              'min-w-[28px] rounded border px-2 py-1 tabular-nums transition-colors',
              p === page
                ? 'border-primary-600 bg-primary-600 font-semibold text-white'
                : 'border-neutral-200 bg-white hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700',
            )}
          >{p}</button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Pagina următoare"
          className={cn(
            'rounded border border-neutral-200 bg-white px-2 py-1 transition-colors dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
            page >= totalPages ? 'cursor-not-allowed opacity-40' : 'hover:bg-neutral-100 dark:hover:bg-neutral-700',
          )}
        >›</button>
      </div>

      {/* Dreapta — page size selector cu label-uri descriptive */}
      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          <label htmlFor="dt-pagesize" className="text-neutral-500 dark:text-neutral-400">Afișează</label>
          <select
            id="dt-pagesize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="cursor-pointer rounded border border-neutral-200 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            aria-label="Rânduri per pagină"
          >
            {pageSizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-neutral-500 dark:text-neutral-400">per pagină</span>
        </div>
      ) : (
        // Spacer pentru a păstra page-numbers centrate când nu există page size selector
        <div aria-hidden className="w-1" />
      )}
    </nav>
  );
}
